import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";

// eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
import { useStore, useActions } from "../../store";
import { actions as LoaderActions } from "../../store/loadingScreen/actions";
import { actions as CartActions } from "../../store/cartDetails/actions";
import { actions as OrderActions } from "../../store/order/actions";

import { clientFetch } from "@flights/client-fetch";

import {
  UIOrder,
  UIPrice,
  UIPriceBreakdown,
  UIPriceChangeResolution,
  UIPriceChangeResolutionResponse,
  UIPriceChangeResponse,
  UIRedirect3DS
} from "@flights/types";

import useGlobalContext from "../../hooks/useGlobalContext";
import useEventTracking, { useTransactionTracking } from "../../hooks/useEventTracking";
import { usePaymentComponent } from "../../hooks/usePaymentComponent";

import useFormatPrice from "../../hooks/useFormatPrice";

import useCreateUrl from "../../hooks/useCreateUrl";
import useContextName from "../../hooks/useContextName";
import { assert } from "../../utils/assert";
import { useCheckoutProcessor } from "../../hooks/useCheckoutProcessor";
import useSearchURL from "../../hooks/useSearchURL";
import { MpStoreState } from "@bookingcom/mp-flights";
import useEvents from "../../hooks/useEvents";
import useClientMetrics from "../../hooks/useClientMetrics";
import { createTimer, flightsSessionStore, pricingUtils } from "@flights/web-api-utils-universal";

import useOfferCartOrderData from "../../hooks/useOfferCartOrderData";
import useCheckoutNavigation from "../../hooks/checkout/useCheckoutNavigation";
import flights_split_bounds_us, {
  splitBoundsTrackingStorage
} from "../../utils/experiments/funnel/flights_split_bounds_us";
import { goal } from "@flights/et-universal";
import useFlightsWebSouthwest from "../../utils/experiments/funnel/flights_web_southwest";
import flights_web_aa_lack_of_choice from "../../utils/experiments/funnel/flights_web_aa_lack_of_choice";
import useProfiler from "../../hooks/useProfiler";

export type CheckoutControllerData = ReturnType<typeof useCheckoutPayment>;

interface Props {
  children: (data: CheckoutControllerData) => React.ReactElement;
}

const useIsCTALoading = (
  isPriceChangeWithoutCharge: boolean,
  pcStatus: string,
  priceChangeVerificationState: boolean
) => {
  let loading = false;

  // If we are on old price change flow, where we check price during /finalize, then we need to reenable the CTA when we get the "isPriceChangeWithoutCharge" state.
  if (!isPriceChangeWithoutCharge) {
    loading = pcStatus === "processing";
  }
  // Alternatively, for the new price change flow, we just check the verification state.
  return loading || priceChangeVerificationState;
};

const useCheckoutPayment = function () {
  const history = useHistory();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const store = useStore();
  const { isMobile } = useProfiler();
  const { jsError } = useEvents();
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const loaderActions = useActions(LoaderActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const cartActions = useActions(CartActions);
  // eslint-disable-next-line import/no-deprecated -- this line was auto generated, hence fix the issue timely
  const orderActions = useActions(OrderActions);
  const { debugAllExtraProducts, requestId } = useGlobalContext();
  const trackV2 = useEventTracking("checkout3", requestId);

  // Used by mp_flights_insurance_mvp experiment
  const [mpState, setMpState] = useState<MpStoreState>({ isLoading: false, multiProducts: [] });

  const searchURLs = useSearchURL();
  const checkout = useCheckoutProcessor();
  const { trackClientMetric } = useClientMetrics();

  const trackTransaction = useTransactionTracking();
  const { createUrl } = useCreateUrl();
  const [retrying, setRetrying] = useState(false);
  const contextName = useContextName();
  const [is3DSPaymentForUS, setIs3DSPaymentForUS] = useState(false);
  const [isPriceChangeWithoutCharge, setPriceChangeWithoutCharge] = useState(false);

  const [priceChangeVerificationState, setPriceChangeVerificationState] = useState(false);
  const [priceChangeProcessingState, setPriceChangeProcessingState] = useState(false);
  const [priceChangeState, setPriceChangeState] = useState<UIPriceChangeResponse | undefined>();
  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false);
  const [priceChangeError, setPriceChangeError] = useState<string>();

  const { grumble } = useEvents();

  const { flight } = store.flightDetails;
  const { order } = store.order;
  const { cartDetails } = store.cartDetails;
  const { priceToValue } = useFormatPrice();

  const flightsWebSouthwest = useFlightsWebSouthwest();

  useCheckoutNavigation();

  if (!order) {
    throw new Error("Missing order data");
  }

  const PRICE_CHANGE_WITHOUT_CHARGE_ERROR = "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED_WITHOUT_CHARGE";

  const {
    orderToken,
    otaCartReference,
    otaCartToken,
    airOrder,
    totalPrice,
    passengers,
    ancillaries,
    includedProductsBySegment
  } = order;

  const orderId = store.paymentCollection.paymentCollection?.orderId || order.orderId;

  useEffect(() => {
    // Destroying store cart to make sure no components would use it on payment step (`order` should be used instead)
    cartActions.reset();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (store.order?.orderCreated?.orderToken && priceChangeProcessingState) {
      const flightToken = store.flightDetails.flight?.token;
      const orderToken = store.order?.orderCreated?.orderToken;
      const url = createUrl(`/checkout/payment/${flightToken}/${orderToken}?priceChange=true`);
      window.location.replace(url);
    }
  }, [
    history,
    store.flightDetails,
    store.order,
    store.cartDetails,
    priceChangeProcessingState,
    orderActions,
    loaderActions,
    createUrl
  ]);

  const goToConfirmation = useCallback(
    ({ paymentCollectionSuccess }: { paymentCollectionSuccess: boolean }) => {
      const paymentCollectionUnits = store.paymentCollection?.paymentCollection?.remainingAmount?.units;
      const paymentCollectionNanos = store.paymentCollection?.paymentCollection?.remainingAmount?.nanos;

      const params = new URLSearchParams({
        isPaymentCollection: "1",
        paymentCollectionUnits: typeof paymentCollectionUnits === "number" ? paymentCollectionUnits.toString() : "",
        paymentCollectionNanos: typeof paymentCollectionNanos === "number" ? paymentCollectionNanos.toString() : "",
        paymentCollectionSuccess: paymentCollectionSuccess ? "1" : "0"
      });

      const path = `/booking/confirmation/${orderToken}?${params}`;
      history.replace(createUrl(path));
    },
    [history, orderToken, createUrl, store.paymentCollection]
  );

  const goToPriceChanged = useCallback(
    (code: string) => {
      const params = new URLSearchParams();
      if (flight) {
        params.set("flightToken", flight.token);
      }
      if (otaCartReference) {
        params.set("reference", otaCartReference);
      }
      if (otaCartToken) {
        params.set("cartToken", otaCartToken);
      }

      /**
       * There are two cases here, if we haven't deducted the amount from the user yet we will be showing the price change modal on the payment screen
       * itself which is the case without charge. Else the user lands into the old price change flow on a new screen.
       */

      if (code === PRICE_CHANGE_WITHOUT_CHARGE_ERROR) {
        setPriceChangeWithoutCharge(true);
      } else {
        history.replace(createUrl(`/checkout/pricechange/${orderToken}?${params}`));
        window.location.reload();
      }
    },
    [flight, otaCartReference, otaCartToken, history, createUrl, orderToken]
  );

  const postToUrl = (url: string, params: { [k: string]: string }) => {
    const form = document.createElement("form");
    form.action = url;
    form.method = "POST";
    for (const i in params) {
      if (params.hasOwnProperty(i)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = i;
        input.value = params[i];
        form.appendChild(input);
      }
    }
    document.body.appendChild(form);
    form.submit();
  };

  const handleSupplier3DsRedirect = useCallback((redirect3dsData: UIRedirect3DS) => {
    const { redirectUrl, bodyParams } = redirect3dsData;
    setIs3DSPaymentForUS(true);
    postToUrl(redirectUrl, bodyParams);
  }, []);

  const offerCartOrderData = useOfferCartOrderData();

  const finalize = useCallback(() => {
    const priceChangeErrorCodes = [
      "CART_MANAGEMENT_FINALIZEORDER_CART_PRICE_CHANGED",
      PRICE_CHANGE_WITHOUT_CHARGE_ERROR
    ];
    let orderFinalizeError: any;
    let redirect3dsData: UIRedirect3DS | undefined;
    let paymentCollectionSuccess: boolean;
    loaderActions.show();

    //landingUrl3ds is required for US PointofSale where the payment is handled on etg side (US)
    const data = {
      landingUrl3ds: location.href
    };

    let finalizePostBookingPaymentUrl = `/api/order/finalizePostBookingPayment/${orderToken}`;

    const addOnOrderId = store.paymentCollection?.paymentCollection?.addOnOrderId;
    if (addOnOrderId) {
      finalizePostBookingPaymentUrl += `?addOnOrderId=${addOnOrderId}`;
    }

    grumble("Calling finalize post booking order", { orderId });
    return clientFetch<UIOrder>(finalizePostBookingPaymentUrl, {
      method: "post",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName,
        "X-Booking-Experiment-Prepare-Clientside-Payload": "1"
      },
      body: JSON.stringify(data)
    })
      .then((data) => {
        const flightDetails = data.airOrder.flightSegments[0];
        const itinerary = `${flightDetails.departureAirport.code}-${flightDetails.arrivalAirport.code}`;
        const { total, tax } = data.totalPrice;
        redirect3dsData = data.redirect3dsData;
        orderActions.fetchSuccess(data);

        const paymentStatus = data.paymentStatus;
        paymentCollectionSuccess = paymentStatus !== "PAYMENT_ERROR";

        if (data.orderStatus == "CONFIRMED" || data.orderStatus == "PENDING") {
          void trackTransaction({
            id: data.orderId,
            obfuscatedOtaOrderReference: data.obfuscatedOtaOrderReference,
            itinerary,
            revenue: priceToValue(total),
            tax: priceToValue(tax),
            currency: total.currencyCode,
            partner: data.salesInfo?.partner,
            maskedUserEmail: data.booker.maskedCustomerEmail,
            maskedPhoneNumber: data.booker.maskedCustomerPhoneNumber
          });
        }
      })
      .catch((error) => {
        paymentCollectionSuccess = false;
        orderFinalizeError = error;
      })
      .finally(() => {
        /**
         * There can be 3 cases here:
         * 1. For US POS in case there is a 3Ds payment we need to redirect to supplier3Ds page
         * 2. Order Finalized can give price change error and we need to redirect user to price change screen
         * 3. Order can get confirmed and user needs to be redirected to confirmation screen. This can happen at different instances:
         *    3.1 In case of US POS Finalized is called for the second time after supplier3Ds is successful and user is redirected to confirmation
         *    3.2 In case of usual 3Ds payment Finalized is called after 3Ds is successful and user is redirected to confirmation
         *    3.3 Finalized is called after a non3Ds payment and user is redirected to confirmation
         */
        if (redirect3dsData && redirect3dsData.redirectUrl) {
          grumble("Redirecting to 3DS", { redirect3dsData, orderId });
          handleSupplier3DsRedirect(redirect3dsData);
        } else if (priceChangeErrorCodes.includes(orderFinalizeError?.code)) {
          loaderActions.hide();
          goToPriceChanged(orderFinalizeError?.code);
        } else {
          if (orderFinalizeError) {
            jsError(orderFinalizeError);
          }
          loaderActions.hide();
          /**
           * In case of redirect3Ds flow from the supplier we should not redirect to confirmation page, if we have redirectUrl for 3dsecure.
           * In this case the form submit needs to happen to take user to the 3ds page.
           * Only when finalized is called for the second time post redirection back from 3dSecure we should go to confirmation.
           */

          !is3DSPaymentForUS && goToConfirmation({ paymentCollectionSuccess });
        }
      });
  }, [
    loaderActions,
    orderToken,
    contextName,
    orderActions,
    trackTransaction,
    handleSupplier3DsRedirect,
    goToPriceChanged,
    is3DSPaymentForUS,
    goToConfirmation,
    priceToValue,
    jsError,
    grumble,
    orderId,
    store.paymentCollection
  ]);

  const handlePaymentFailed = useCallback(() => {
    return Promise.resolve();
  }, []);

  const paymentId = store.paymentCollection.paymentCollection?.paymentId || (airOrder && airOrder.paymentId);
  const paymentComponentProductType = order?.supplierInfo?.paymentComponentProductType;

  const { pcContainerRef, pcProcessPayment, pcStatus, pcIsLoading, pcIsLoaded } = usePaymentComponent({
    paymentId,
    paymentComponentProductType,
    successfulCallback: finalize,
    pendingCallback: finalize,
    failedCallback: handlePaymentFailed,
    orderId,
    type: "order"
  });

  const isCTALoading = useIsCTALoading(isPriceChangeWithoutCharge, pcStatus, priceChangeVerificationState);

  const redirectToSearch = useCallback(() => {
    location.href = `${location.origin}${searchURLs?.searchresults}`;
  }, [searchURLs]);

  const redirectToFlightDetails = useCallback(
    (token: string) => {
      const searchResultsUrl = searchURLs?.searchresults;

      if (!searchResultsUrl) {
        throw new Error("Alternative Fares Redirect: Search Results URL was not found");
      }

      const { origin, pathname, search } = new URL(searchResultsUrl);
      const newUrl = new URL(`${pathname}${token}/${search}`, origin);

      location.href = newUrl.href;
    },
    [searchURLs]
  );

  /** Verify with ETG if the price changed before capturing the payment. */
  // eslint-disable-next-line
  const verifyPriceChange = useCallback(() => {
    const addOnOrderId = store.order.addOnOrder?.addOnOrderId;
    setPriceChangeVerificationState(true);

    let verifyPriceChangeUrl = `/api/order/verifyPriceChange/${orderToken}`;
    if (addOnOrderId) {
      verifyPriceChangeUrl += `?addOnOrderId=${addOnOrderId}`;
    }

    const timer = createTimer();
    clientFetch<UIPriceChangeResponse>(verifyPriceChangeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName
      }
    })
      .then((priceChangeResponse) => {
        setPriceChangeVerificationState(false);
        const time = timer();
        trackClientMetric("verify_price_change_latency", time);
        if (priceChangeResponse.status === "AWAITING_PAYMENT") {
          pcProcessPayment();
        } else if (priceChangeResponse.status === "PRICE_CHANGE") {
          setPriceChangeState(priceChangeResponse);
          setShowPriceChangeModal(true);
        } else {
          setPriceChangeState(undefined);
          setPriceChangeError("Order is not available during price change verification.");
        }
      })
      .catch(() => {
        setPriceChangeError("Error during order price change verification");
        setPriceChangeVerificationState(false);
      });
  }, [store.order, contextName, orderToken, pcProcessPayment, trackClientMetric]);

  const createUpdatedPriceOrder = useCallback(async () => {
    setPriceChangeProcessingState(true);

    await checkout.createOrder(cartDetails?.cart, {
      priceChangeOrderId: order.orderId,
      basketReference: cartDetails?.basketReference
    });
  }, [checkout, order, cartDetails]);

  type HandlePriceChangeParams<T extends UIPriceChangeResolution> = T extends "ACCEPTED"
    ? [resolution: "ACCEPTED"]
    : [resolution: "DECLINED", token?: string];

  const handlePriceChange = useCallback(
    <T extends UIPriceChangeResolution>(...[resolution, token]: HandlePriceChangeParams<T>) => {
      const addOnOrderId = store.order.addOnOrder?.addOnOrderId;

      assert(priceChangeState);

      const payload: { resolution: UIPriceChangeResolution; acceptedPrice?: UIPrice } = {
        resolution
      };

      if (resolution === "ACCEPTED") {
        payload.acceptedPrice = priceChangeState.newPrice;
      }

      setPriceChangeProcessingState(true);

      let handlePriceChangeUrl = `/api/order/handlePriceChange/${orderToken}`;

      if (addOnOrderId) {
        handlePriceChangeUrl += `?addOnOrderId=${addOnOrderId}`;
      }

      clientFetch<UIPriceChangeResolutionResponse>(handlePriceChangeUrl, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "X-Flights-Context-Name": contextName
        }
      })
        .then((handlePriceChangeResponse) => {
          setPriceChangeProcessingState(false);

          if (handlePriceChangeResponse.status === "AWAITING_PAYMENT") {
            assert(handlePriceChangeResponse.otaCartReference);
            assert(handlePriceChangeResponse.otaCartToken);
            window.location.reload();
          } else if (handlePriceChangeResponse.status === "CANCELLED") {
            setPriceChangeState(undefined);
            token ? redirectToFlightDetails(token) : redirectToSearch();
          } else {
            setPriceChangeState(undefined);
            setPriceChangeError(`Unknown error when handling price change: ${handlePriceChangeResponse.status}`);
          }
        })
        .catch((error) => {
          setPriceChangeState(undefined);
          setPriceChangeProcessingState(false);
          setPriceChangeError("Something went wrong when handling the ");
          throw error;
        });
    },
    [
      store.order.addOnOrder?.addOnOrderId,
      priceChangeState,
      orderToken,
      contextName,
      redirectToFlightDetails,
      redirectToSearch
    ]
  );

  const onPayClick = useCallback(() => {
    goal("flights_checkout_payment_paynow_click");
    flightsWebSouthwest.trackCustomGoal(); // flights_web_southwest

    /* start - flights_split_bounds_us */
    if (flight) {
      const trackingData = splitBoundsTrackingStorage.getValue(flight?.token);
      if (trackingData?.price) {
        flights_split_bounds_us.goals.negative_incremental_price_pay_now_click();
      }

      if (trackingData?.genius) {
        flights_split_bounds_us.goals.genius_offer_pay_now_click();
      }
    }
    /* end - flights_split_bounds_us */

    trackV2("click_pay_now", {
      order_id: orderId,
      payment_id: paymentId
    });

    if (flightsSessionStore.get("flights_web_aa_lack_of_choice_first_page_clicked")) {
      flights_web_aa_lack_of_choice.getExp(isMobile()).goals.firstPageClickAndPay();
    }
    if (flightsSessionStore.get("flights_web_aa_lack_of_choice_top_five_clicked")) {
      flights_web_aa_lack_of_choice.getExp(isMobile()).goals.topFiveClickAndPay();
    }

    pcProcessPayment();
  }, [flight, trackV2, orderId, paymentId, pcProcessPayment, flightsWebSouthwest, isMobile]);

  const onRetry = useCallback(async () => {
    setRetrying(true);
    // initialize order
    const orderData = {
      /*eslint-disable-next-line flights/no-unassigned-todo-comments*/
      // TODO: deprecated
      cartReference: otaCartReference,
      cartToken: otaCartToken
    };
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    orderData["payment3DsRedirectUrl"] = decodeURI(
      // `<ORDER_TOKEN>` is a template replaced on java layer
      createUrl(`${window.location.origin}/checkout-collection/<ORDER_TOKEN>`)
    );

    const orderResponse = await clientFetch<any>("/api/order", {
      method: "post",
      credentials: "same-origin",
      body: JSON.stringify(orderData),
      headers: {
        "Content-Type": "application/json",
        "X-Flights-Context-Name": contextName,
        ...(debugAllExtraProducts ? { "X-Flights-Debug-All-Products": "1" } : {})
      }
    }).then((r) => r);

    // process order response
    const { orderId, error, orderToken } = orderResponse;

    if (error) {
      setRetrying(false);
      throw error;
    }

    if (!orderId) {
      setRetrying(false);
      throw new Error("No order id");
    }

    offerCartOrderData.set(otaCartReference, { order: orderToken });
    location.reload();
  }, [otaCartReference, otaCartToken, contextName, debugAllExtraProducts, createUrl, offerCartOrderData]);

  const totalPriceWithMP = useMemo((): UIPriceBreakdown => {
    const multiProductPrices = (mpState && mpState.multiProducts.map((item) => item.priceBreakdown)) || [];
    return pricingUtils.sumPriceBreakdown([totalPrice, ...multiProductPrices]);
  }, [mpState, totalPrice]);

  return {
    flight,
    includedProductsBySegment,
    passengers,
    totalPrice: totalPriceWithMP,
    ancillaries,
    travellerPrices: airOrder.passengerPrices,
    appliedSubsidizedFares: airOrder.appliedSubsidizedFares,
    pcContainerRef,
    onPayClick,
    pcStatus,
    onRetry,
    pcIsLoading,
    pcIsLoaded,
    isCTALoading,
    retrying,
    order,
    redirectToSearch,
    createUpdatedPriceOrder,
    handlePriceChange,
    priceChangeState,
    showPriceChangeModal,
    priceChangeError,
    isPriceChangeWithoutCharge,
    priceChangeProcessingState,
    priceChangeVerificationState,
    mpState,
    setMpState
  };
};

const CheckoutPaymentController: React.FunctionComponent<Props> = (props) => {
  const checkoutData = useCheckoutPayment();

  return props.children(checkoutData);
};

export default CheckoutPaymentController;
